import type { DynamicPageProps } from '@/core/features/a-dynamic-page/dynamic-page-types';

import React from 'react';

import { IS_SERVER } from '@app/core-features/env/env-shared';
import debounce from 'lodash.debounce';

import ChevronRightSmallIcon from '@/assets/icons/chevron-right-small';
import NotificationWrapper from '@/core/components/notification/notification-wrapper';
import { ButtonWrapper, LoopedLoadingBar } from '@/core/features';
import { useClientUrl } from '@/core/features/app/app-atoms';
import useClientOnlyRender from '@/core/features/render/use-client-only-render';
import {
    restorePageScrollPosition,
    savePageScrollPosition,
} from '@/core/features/scroll/page-scroll-restoration/scroll-history';
import { scrollToTop } from '@/core/features/scroll/scroll-service';
import useHasInteractedRef from '@/core/features/scroll/use-has-interacted-ref';
import { atom, useAtom } from '@/core/features/store/atom-store';
import color from '@/core/features/styles/color';
import PageComponentSwitchWrapperDesktop from '@/features/a-dynamic-page/components/page-component-switch-wrapper-desktop';
import { useGoogleMapInstance } from '@/features/map/google-map/google-map-state';
import MapComponentDesktop from '@/features/map/map-container/map-component-desktop';
import { useMapLoadingState } from '@/features/map/map-container/map-state';
import DesktopContentWrapper from '@/features/page-layouts/desktop/desktop-content-wrapper';
import { idC24IndiPageContainerContent } from '@/features/page-layouts/desktop/desktop-layout-wrapper';

export const showMainContentAtom = atom(true);

export default function DesktopMapSearchLayout(props: DynamicPageProps) {
    const [showMainContent, setShowMainContent] = useAtom(showMainContentAtom);
    const shouldRenderMap = useClientOnlyRender();
    const isMapLoading = useMapLoadingState();
    const [clientUrl] = useClientUrl();
    const windowRef = React.useRef(IS_SERVER ? null : window);
    const hasInteractedRef = useHasInteractedRef(windowRef);

    //FIXME: dont use pageType
    const useLegacyMinWidth = props.meta?.pageType === 'activity';

    const onMapResize = () => {
        if (showMainContent) {
            savePageScrollPosition();
        }
        if (!showMainContent) {
            restorePageScrollPosition(clientUrl, { hasInteractedRef });
        }
        scrollToTop('instant');
        setShowMainContent(!showMainContent);
    };
    const googleMapInstance = useGoogleMapInstance();
    // handle browser back scrolling to top becuase map is initializing
    const isInitializing = React.useRef(false);
    React.useEffect(() => {
        isInitializing.current = true;
        setTimeout(() => {
            isInitializing.current = false;
        }, 2000);
    }, []);
    const onMapInteraction = React.useCallback(() => {
        clearScollToTopTimeout();
        if (isInitializing.current) {
            return;
        }
        scrollToTopTimeoutRef.current = setTimeout(() => {
            scrollToTop();
        }, 1000);
    }, []);
    /**
     * Effect: cleanup scroll to top timeout
     */
    const clearScollToTopTimeout = () => {
        if (scrollToTopTimeoutRef.current !== null) {
            clearTimeout(scrollToTopTimeoutRef.current);
        }
    };
    React.useEffect(() => {
        const scrollHandler = debounce(
            () => {
                clearScollToTopTimeout();
            },
            200,
            { leading: true },
        );
        window.addEventListener('scroll', scrollHandler);
        return () => {
            window.removeEventListener('scroll', scrollHandler);
            clearScollToTopTimeout();
        };
    }, []);
    const scrollToTopTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    React.useEffect(() => {
        if (!googleMapInstance) {
            return;
        }
        const zoomChangeListener = googleMapInstance.addListener('zoom_changed', onMapInteraction);
        const dragendListener = googleMapInstance.addListener('dragend', onMapInteraction);
        return () => {
            google.maps.event.removeListener(zoomChangeListener);
            google.maps.event.removeListener(dragendListener);
        };
    }, [googleMapInstance, onMapInteraction]);

    const headerComponents = props.components.filter((components) => components.meta.layoutPosition === 'header');
    const asideComponents = props.components.filter((component) => component.meta.layoutPosition === 'aside');
    const mainContentComponents = props.components.filter(
        (component) => component.meta.layoutPosition === 'main_content',
    );
    const mapDataComponent = props.components.find((component) => component['@type'] === 'MapDataV5');

    return (
        <>
            <DesktopContentWrapper isPageLoading={props.isPageLoading}>
                <div
                    className={'flex'}
                    id={idC24IndiPageContainerContent}
                >
                    <div
                        className={
                            'desktopMapSearchLayoutMainLeft flex flex-col w-full pt-10 pb-15 pl-15 pr-15 gap-15 flex-1'
                        }
                        style={{ minWidth: showMainContent ? (useLegacyMinWidth ? 995 : 849) : undefined }}
                    >
                        {headerComponents.length > 0 && (
                            <div className={`mb-5 ${showMainContent ? 'block' : 'hidden'}`}>
                                <div className={'flex flex-col gap-15 w-full'}>
                                    <PageComponentSwitchWrapperDesktop
                                        {...props}
                                        components={headerComponents}
                                    />
                                </div>
                            </div>
                        )}
                        <div className={'flex gap-15'}>
                            {asideComponents.length > 0 && (
                                <div
                                    className={`flex flex-col gap-15 ${!showMainContent ? 'mt-5' : ''}`}
                                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                                    style={{ maxWidth: 254, minWidth: 254 }}
                                >
                                    <PageComponentSwitchWrapperDesktop
                                        {...props}
                                        components={asideComponents}
                                    />
                                </div>
                            )}
                            {mainContentComponents.length > 0 && (
                                <div
                                    className={`relative w-full ${!showMainContent ? 'hidden' : ''}`}
                                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                                    style={{ minWidth: 550 }}
                                >
                                    <div className={'flex flex-col gap-15'}>
                                        <PageComponentSwitchWrapperDesktop
                                            {...props}
                                            components={mainContentComponents}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div
                        className={`desktopMapSearchLayoutMainRight sticky top-0 flex items-center justify-center h-dvh ${!showMainContent ? 'isMainContentHidden' : ''}`}
                    >
                        <ButtonWrapper
                            className={'flex absolute justify-center items-center bg-white cursor-pointer rounded-5'}
                            height={40}
                            onClick={onMapResize}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ left: 20, top: 20, zIndex: 20 }}
                            width={40}
                        >
                            <span
                                className={'flex justify-center items-center bg-white'}
                                data-qa-id={'qa-map-split-toggle-button'}
                            >
                                <ChevronRightSmallIcon
                                    color={color('black-09')}
                                    height={15}
                                    rotate={showMainContent ? 180 : 0}
                                    width={15}
                                />
                            </span>
                        </ButtonWrapper>
                        <div className={'w-full'}>
                            {mapDataComponent && shouldRenderMap && (
                                <div className={'h-full h-dvh bg-white overflow-hidden'}>
                                    {isMapLoading && (
                                        <LoopedLoadingBar
                                            className={'absolute w-full top-0'}
                                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                                            style={{ left: 16 }}
                                        />
                                    )}
                                    <MapComponentDesktop />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </DesktopContentWrapper>
            <NotificationWrapper />
            <style jsx>{`
                .desktopMapSearchLayoutMainLeft {
                    max-width: 50%;
                }
                .desktopMapSearchLayoutMainRight {
                    min-width: 355px;
                    width: 50%;
                }
                .desktopMapSearchLayoutMainRight.isMainContentHidden {
                    width: 100%;
                }
                @media only screen and (max-width: 1364px) {
                    .desktopMapSearchLayoutMainLeft {
                        max-width: unset;
                    }
                    .desktopMapSearchLayoutMainRight {
                        display: none;
                    }
                    .desktopMapSearchLayoutMainRight.isMainContentHidden {
                        display: flex;
                        width: 100%;
                    }
                }
            `}</style>
        </>
    );
}
